import React, { useEffect, useState } from 'react';

// ★（トラブル中・要対応必須などの目印）。店舗一覧と店舗ダッシュボードで共通。
// 保存は AnalyticsController#star。区分は必須、理由は任意（2026-10-20 衣里さん決定）。

export const starTitle = (star) => (star
  ? [`★ ${star.kindLabel}`, star.memo || '（理由の入力なし）', [star.by, star.at].filter(Boolean).join('・')].filter(Boolean).join('\n')
  : '★を付ける（トラブル中・要対応必須などの目印）');

export function StarButton({ star, onClick }) {
  return (
    <button type="button" className={`sl-star${star ? ' on' : ''}`} title={starTitle(star)}
            aria-label={star ? '★の内容を見る・外す' : '★を付ける'}
            onClick={(e) => { e.stopPropagation(); onClick(); }}>{star ? '★' : '☆'}</button>
  );
}

// 付ける・区分や理由を直す・外す。保存できたら onSaved(★ または null)
// ★の付いている店は、まず登録内容だけを見せ（衣里さんの画面イメージ 2026-10-20）、「編集」で入力欄に切り替える。
export function StarDialog({ store, star, kinds, url, onClose, onSaved }) {
  const [editing, setEditing] = useState(!star);
  const [kind, setKind] = useState(star ? String(star.kind || '') : String(kinds[0].value));
  const [memo, setMemo] = useState(star ? star.memo : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const save = (on) => {
    if (on && !kind) { setError('区分を選んでください'); return; }
    setBusy(true); setError('');
    const token = document.querySelector('meta[name="csrf-token"]');
    fetch(url, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': token ? token.content : '' },
      body: JSON.stringify(on ? { star: 1, kind: Number(kind), memo } : { star: 0 })
    })
      .then((res) => res.json().then((json) => ({ ok: res.ok, json })))
      .then(({ ok, json }) => {
        if (ok) { onSaved(json.star || null); return; }
        setBusy(false); setError(json.reason || '保存できませんでした');
      })
      .catch(() => { setBusy(false); setError('保存できませんでした。画面を読み込み直してください'); });
  };

  const name = (
    <div className="sl-star-store"><b>{store.name}</b>{store.code ? `（${store.code}）` : ''}</div>
  );

  if (!editing) {
    return (
      <>
        <div className="sd-ov" onClick={onClose} />
        <div className="sd-dialog sl-star-dialog" role="dialog" aria-modal="true" aria-label="★の内容">
          <div className="sd-dh">
            <span>★の内容</span>
            <button type="button" className="sd-x" onClick={onClose} aria-label="閉じる">×</button>
          </div>
          <div className="sd-db">
            <div>
              {name}
              <dl className="sl-star-dl">
                <dt>区分</dt><dd>{star.kindLabel}</dd>
                <dt>理由</dt><dd>{star.memo || '（理由の入力なし）'}</dd>
                <dt>付けた人</dt><dd>{star.by || '—'}{star.at ? `（${star.at.slice(0, 10)}）` : ''}</dd>
              </dl>
            </div>
            {error && <div className="sl-star-error">{error}</div>}
          </div>
          <div className="sd-df">
            <button type="button" className="sd-btn sd-ghost" disabled={busy} onClick={() => save(false)}>★を外す</button>
            <button type="button" className="sd-btn sd-ghost" disabled={busy} onClick={() => { setError(''); setEditing(true); }}>編集</button>
            <button type="button" className="sd-btn sd-primary" disabled={busy} onClick={onClose}>閉じる</button>
          </div>
        </div>
      </>
    );
  }

  // 編集のキャンセルは、入れかけた内容を捨てて登録内容の表示に戻る
  const cancel = star
    ? () => { setKind(String(star.kind || '')); setMemo(star.memo); setError(''); setEditing(false); }
    : onClose;

  return (
    <>
      <div className="sd-ov" onClick={onClose} />
      <div className="sd-dialog sl-star-dialog" role="dialog" aria-modal="true" aria-label="★">
        <div className="sd-dh">
          <span>{star ? '★の内容を編集' : '★を付ける'}<span className="sd-sub">{[store.code, store.name].filter(Boolean).join(' ')}</span></span>
          <button type="button" className="sd-x" onClick={onClose} aria-label="閉じる">×</button>
        </div>
        <div className="sd-db">
          {!star && <div className="sd-note">★を付けると店舗一覧の上に出て、店舗ダッシュボードにも区分と理由が出ます。</div>}
          <label className="sl-star-label">区分
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </label>
          <label className="sl-star-label">理由（任意）
            <textarea rows="4" value={memo} onChange={(e) => setMemo(e.target.value)}
                      placeholder="例：口コミの低評価が続き、店舗と対応中" />
          </label>
          {star && <div className="sd-note">付けた人：{[star.by, star.at].filter(Boolean).join('・') || '—'}</div>}
          {error && <div className="sl-star-error">{error}</div>}
        </div>
        <div className="sd-df">
          {star && <button type="button" className="sd-btn sd-ghost sl-star-off" disabled={busy} onClick={() => save(false)}>★を外す</button>}
          <button type="button" className="sd-btn sd-ghost" disabled={busy} onClick={cancel}>キャンセル</button>
          <button type="button" className="sd-btn" disabled={busy} onClick={() => save(true)}>{star ? '保存' : '★を付ける'}</button>
        </div>
      </div>
    </>
  );
}
